import { Client } from '@colyseus/sdk';
import {
  CLIENT_MESSAGE,
  ROOM,
  SERVER_MESSAGE,
  WorldState,
  type AttackPayload,
  type DescendPayload,
  type InvitePayload,
  type MoveToPayload,
  type PickUpPayload,
  type ProgressionPayload,
  type RunTollPayload,
  type RunVisibility,
  type SpendSkillPointPayload,
  type UseSkillPayload,
} from '@d2/schema';

import { type LevelDescriptor } from './cave';
import { type CharacterVitals, type ProgressionLedger } from './character-feed';
import { type PendingIntent } from './prediction';
import { type FloorSize, createOptions, joinOptions } from './room-options';
import { type RunToll } from './run-end';
import { type WorldSnapshot } from './session';
import { readLevel, readRunToll, readVitals, readWorld } from './wire';

/**
 * The socket, and nothing but the socket.
 *
 * Everything this client does with the room is decided in pure modules —
 * `./session` folds snapshots, `./cave` verifies the floor, `./prediction` and
 * `./interpolation` draw. This is the adapter that turns those into a Colyseus
 * connection: it matchmakes, waits until the room has described its floor,
 * decodes state into the plain shapes `./wire` defines, and puts an intent on
 * the wire. It holds no state of its own beyond the room handle and it makes no
 * decision that could be wrong in an interesting way — which is why it is the
 * one client module the unit suite does not reach and the browser suite proves
 * instead.
 *
 * ## Create, or join by id — never `joinOrCreate`
 *
 * A `joinOrCreate` would drop every client into whichever descent room happened
 * to have a seat, which is the right lobby behaviour and completely wrong for
 * this milestone: two browsers must be able to land in the *same* room on
 * purpose, and a third must be able to land somewhere else. So the two cases
 * are explicit — a client either creates a floor (offering the seed it was
 * launched with, which the room validates like anything else a client says) or
 * joins one that already exists by id.
 *
 * **That is still true now that there is a lobby**, and the lobby is why it
 * matters more rather than less. A room id no longer comes only from a `?room=`
 * URL: it comes from `GET /runs/:code`, answered against a code the player
 * clicked or typed (`ui/lobby-board.tsx`). What has not changed is that this
 * client is told *which* room and asks for that one — nobody here picks a room
 * for a player, so a party of four cannot be handed a fifth seat by a
 * `joinOrCreate` that saw one going spare.
 *
 * ## Nothing is trusted in this direction either
 *
 * The room's answer is decoded by `@colyseus/schema` into `WorldState`, and the
 * one claim in it that this client can check — the grid hash — it checks, in
 * `./cave`, before a tile is drawn. That check is the caller's to make because
 * the caller is the one that has to decide what to *show* when it fails.
 *
 * ## The options are two things at once, and they are kept apart
 *
 * Colyseus hands the *same* options object to `onCreate` and to `onAuth`
 * (`MatchMaker.create` → `reserveSeatFor` → `Room._onJoin`), so one object has
 * to carry both what this floor should be and who is asking to sit on it.
 * `./room-options` is what decides its shape — credentials nested under their
 * own key, the create's requests beside them — and the *why* lives there.
 *
 * Nothing about it is decided in this file, which is what keeps this module the
 * adapter it claims to be: it matchmakes with whatever that pure module built,
 * and the shape it built is unit-tested without a socket.
 */

/** Where to connect, and who is asking. */
export interface RoomLinkRequest {
  /** The Colyseus endpoint, e.g. `ws://localhost:2567`. */
  readonly endpoint: string;
  /** An existing room to join by id, or `null` to create one. */
  readonly roomId: string | null;
  /**
   * The player's access token, forwarded to `onAuth` untouched.
   *
   * The room verifies it against the key its own process was started with and
   * then hands it to the API, which is what decides whether this account owns
   * the character. Nothing here inspects it: a token is an opaque bearer to this
   * package, and `@d2/schema/tokens` is deliberately not re-exported from the
   * package index so a JWT library never reaches a browser bundle.
   */
  readonly token: string;
  /** Which character to be seated as. The room asks the API whether it is this account's. */
  readonly characterId: string;
  /**
   * What to ask a **created** room for: its floor, and — for the browser suite
   * alone — how much is standing on it and how big it is (`./room-options`).
   *
   * Ignored when `roomId` names a room that already exists, because that room's
   * floor was decided before this client arrived.
   */
  readonly seed: number;
  readonly population: number | null;
  readonly size: FloorSize | null;
  readonly fixtureKey: string | null;
  /**
   * Who may find the run this client is **creating**, or `null` to say nothing
   * — which the server reads as private (`./room-options`).
   *
   * Ignored when `roomId` names a room that already exists, exactly as the seed
   * is: whether that run is advertised was decided by whoever created it and is
   * not a thing an arriving player gets a vote on.
   */
  readonly visibility: RunVisibility | null;
}

/** A joined room, as the rest of the client needs it. */
export interface RoomLink {
  /** This client's Colyseus session id — the key its own entity is filed under. */
  readonly localId: string;
  /** The room's id, which is what a second browser needs to land in this same floor. */
  readonly roomId: string;
  /** The floor the room is on **at the moment of the join**, as the ~40 bytes it crossed the wire as. */
  readonly level: LevelDescriptor;
  /** The first snapshot, already decoded — the one a join is answered with. */
  readonly initial: WorldSnapshot;
  /** Every snapshot after that, stamped with the browser time it landed at. */
  onSnapshot: (handler: (snapshot: WorldSnapshot, localTimeMs: number) => void) => void;
  /**
   * The floor, off **every** patch that carries one.
   *
   * A third reader of the same `onStateChange`, and it exists because a floor is
   * no longer a thing decided once before anybody joined: a descent rewrites
   * `LevelState` mid-session and every seat is standing on a different cave a
   * tick later (`apps/game`'s `advanceTo`).
   *
   * It reports the level on every patch rather than only when it changed,
   * because deciding *whether* it changed is a comparison, and a comparison is a
   * decision — which this module is deliberately incapable of getting wrong by
   * not making any. `./cave`'s `sameFloor` is where that lives, in a pure module
   * with a test.
   *
   * **It is called before `onSnapshot`, and the order is load-bearing.** One
   * patch carries the new level, the reseated bodies and the new floor's
   * population together, and the room mints monster ids per floor from `m0` up —
   * so a snapshot folded in before the floor was rebuilt would file the new
   * floor's first body into the old floor's first body's interpolation buffer
   * (`session.ts`'s `enterFloor`).
   */
  onFloor: (handler: (level: LevelDescriptor) => void) => void;
  /**
   * This seat's own character, off every snapshot that describes it.
   *
   * The same patches `onSnapshot` carries, read for a different question: that
   * one asks where the bodies are, this one asks how *this* character is doing
   * (`./wire`'s `readVitals`). One subscription serves both, so the two can
   * never be a patch out of step with each other.
   */
  onCharacter: (handler: (vitals: CharacterVitals) => void) => void;
  /**
   * The seat's own point ledger, whenever the server reports it.
   *
   * A message and not state, because it is addressed to one connection rather
   * than broadcast to the party — `@d2/schema`'s `ProgressionPayload` argues the
   * placement at length. Sent on seat and thereafter only when the ledger
   * changes, never on a tick.
   */
  onProgression: (handler: (ledger: ProgressionLedger) => void) => void;
  /**
   * What this seat's death cost it, whenever the room says so.
   *
   * A message and not state, for the reason `onProgression` is one and a
   * stronger one besides: **what a player lost is nobody else's business**, and
   * `WorldState` is a broadcast to the whole party (`@d2/schema`'s
   * `RunTollPayload`). Sent once, to one seat, on the way out of the run it
   * ended.
   *
   * It is deliberately **not** folded into `onClosed`, even though the two are a
   * moment apart and every caller wants them together. Pairing them means
   * holding the message until the close arrives, and *"which toll belongs to
   * this close"* is a decision — which this module is incapable of getting wrong
   * only because it makes none. It is also the one client module this package's
   * unit suite does not reach. So the message goes up as it lands and
   * `game-canvas.tsx` does the latching, where a test can watch it.
   */
  onRunToll: (handler: (toll: RunToll) => void) => void;
  /**
   * The code this run is invited by, told to this seat as it sat down.
   *
   * A message rather than something read off the room handle, and that is the
   * SDK's doing rather than a choice: `consumeSeatReservation` keeps the room
   * id, the session id and the room name and **drops the matchmaking metadata**,
   * which is where the code lives (`@d2/schema`'s `InvitePayload`). Without it
   * the one player who could invite anybody — whoever created the run — is the
   * one player who cannot see the code.
   *
   * It arrives once per seat, at the join, and never changes for the life of
   * the room. Handed on **unparsed**, exactly as `onProgression` is: this is the
   * server describing its own room rather than a client saying something, and
   * `parseInviteCode` on this side would be a second opinion about a value this
   * client only prints.
   */
  onInvite: (handler: (code: string) => void) => void;
  /**
   * The socket closed, and this is the code it closed with.
   *
   * Every close the SDK does **not** try to reconnect through arrives here:
   * this client's own consented leave, a room that shut down, and — the one
   * anybody cares about — the room ending the run (`./run-end`). The code is
   * handed on raw and nothing is decided here, exactly as nothing else is in
   * this file: what 4200 *means* is a pure module's, and this adapter would be
   * the one place a test could not reach it.
   *
   * The reason string is deliberately dropped. It is free text a proxy may
   * truncate, the code is the part the frame always carries, and a caller
   * handed both would sooner or later branch on the wrong one.
   *
   * One handler, like every other channel here, and it may be registered at
   * most once — there is nothing on this side that needs two.
   */
  onClosed: (handler: (code: number) => void) => void;
  /** Puts a predicted click on the wire. */
  send: (intent: PendingIntent) => void;
  /**
   * "Hit that." The other half of a fight, and the mirror of a click: a walk is
   * about ground so it names a cell, a swing is about a **body** so it names the
   * id one is replicated under.
   *
   * Predicts nothing. Whether the attacker can reach that body, whether its last
   * swing has finished, what the blow rolls and what it takes off are all
   * `admitSwing`'s, on the server (`@d2/schema`'s `AttackPayload`).
   */
  attack: (payload: AttackPayload) => void;
  /**
   * "Take that." Names the **thing**, never the cell it lies on: a kill can
   * leave gear, a potion and a pile of coins on one square, and *"pick up at
   * 31,42" could not say which* (`@d2/schema`'s `PickUpPayload`).
   *
   * There is deliberately no destination in it either — where an item lands is
   * core's packing scan, run by the server, and loot binds to a character at
   * pickup.
   */
  pickUp: (payload: PickUpPayload) => void;
  /**
   * "Cast that." The client sends the intent and predicts **nothing** about it:
   * the mana, the roll and the blow are all `admitCast` and `resolveCast`, on
   * the server (`ARCHITECTURE.md` forbids predicting a damage number).
   */
  useSkill: (payload: UseSkillPayload) => void;
  /**
   * "Put a point in that." The one intent whose effect is permanent, which is
   * why its sequence is a replay guard rather than a reconciliation key
   * (`@d2/schema`'s `SpendSkillPointPayload`).
   */
  spendSkillPoint: (payload: SpendSkillPointPayload) => void;
  /**
   * "I am taking the stairs." The smallest intent on this wire, and what it
   * leaves out is the whole design: no cell, because where the stairs are is the
   * floor's; no depth, because the floor below is a pure function of this one;
   * no character, because the descender is the connection
   * (`@d2/schema`'s `DescendPayload`).
   *
   * Predicts nothing. Whether the asker is standing close enough, whether they
   * are alive, and what the floor below is are all the room's.
   */
  descend: (payload: DescendPayload) => void;
  /**
   * Leaves, consented — so the room removes the body rather than holding the
   * seat for the reconnection window a dropped socket gets.
   *
   * Resolves when the socket has actually **closed**, which is the moment the
   * room runs `onLeave` and the character stops being seated. A caller that has
   * to open another connection for the same character has to wait for exactly
   * that, because the room refuses the second one until it happens
   * (`descent.room.ts`, `CHARACTER_SEATED`).
   */
  leave: () => Promise<void>;
}

/**
 * Waits until the room has said what floor it is on.
 *
 * `describeLevel` runs in `onCreate`, before any client can join, so the level
 * rides in the snapshot every join is answered with — but the SDK resolves its
 * join promise on the seat reservation, which can be a patch earlier. An
 * all-zero level is `state.ts`'s own description of "this room has not made its
 * floor yet", so a width of zero is exactly the wait condition, and it is one
 * a live room can never be observed in.
 */
const describedFloor = async (room: { state: WorldState; onStateChange: (handler: (state: WorldState) => void) => unknown }): Promise<void> =>
  new Promise<void>((resolve) => {
    const settled = (state: WorldState): boolean => state.level.width > 0;

    if (settled(room.state)) {
      resolve();
      return;
    }

    room.onStateChange((state: WorldState): void => {
      if (settled(state)) resolve();
    });
  });

/**
 * Joins a descent room and hands back everything the client needs from it.
 *
 * Resolves only once the floor has been described, so a caller never has to
 * cope with a half-populated level — the very thing the hash check would
 * otherwise fail on for a reason that is not a real disagreement.
 */
export const joinRoom = async ({
  endpoint,
  roomId,
  token,
  characterId,
  seed,
  population,
  size,
  fixtureKey,
  visibility,
}: RoomLinkRequest): Promise<RoomLink> => {
  const client = new Client(endpoint);
  const credentials = { token, characterId };

  // Two calls, two option sets, and the difference is real: a create is the one
  // and only moment this client may ask for a floor — or ask to be advertised.
  // See `./room-options`.
  const room =
    roomId === null
      ? await client.create<WorldState>(
          ROOM.descent,
          createOptions({ credentials, seed, population, size, fixtureKey, visibility }),
          WorldState,
        )
      : await client.joinById<WorldState>(roomId, joinOptions(credentials), WorldState);

  await describedFloor(room);

  /**
   * The three readers of one patch.
   *
   * Registered once, here, rather than one `onStateChange` per subscriber. A
   * patch is a single moment and all three questions are asked of it — which
   * floor this is, where every body is, and how this seat's character is doing —
   * so separate subscriptions could be delivered a frame apart and would decode
   * `WorldState` three times for the same bytes.
   */
  let onGround: ((level: LevelDescriptor) => void) | null = null;
  let onWorld: ((snapshot: WorldSnapshot, localTimeMs: number) => void) | null = null;
  let onSeat: ((vitals: CharacterVitals) => void) | null = null;

  room.onStateChange((state: WorldState): void => {
    // **First**, and the order is load-bearing rather than tidy. A descent
    // rewrites the level, reseats every player and repopulates the floor inside
    // one message handler, so all of it arrives in one patch — and monster ids
    // are minted per floor from `m0` up. A snapshot folded in before the floor
    // was rebuilt would append the new floor's first body to the old floor's
    // first body's interpolation buffer, and draw it gliding through the rock
    // between two caves (`session.ts`'s `enterFloor`).
    onGround?.(readLevel(state.level));

    // Read here, at the edge, from the one monotonic source every other
    // timestamp in this client comes from: `clock.ts` refuses two clocks
    // mixed into one estimate, and `Date.now()` can be stepped by NTP or by
    // the user, which the estimator would read as a new timeline.
    onWorld?.(readWorld(state), performance.now());

    const mine = state.players.get(room.sessionId);
    // A patch that does not mention this seat says nothing about it, which is
    // not the same as saying it has nothing. The last reading stands.
    if (mine !== undefined) onSeat?.(readVitals(mine));
  });

  return {
    localId: room.sessionId,
    roomId: room.roomId,
    level: readLevel(room.state.level),
    initial: readWorld(room.state),

    onSnapshot: (handler: (snapshot: WorldSnapshot, localTimeMs: number) => void): void => {
      onWorld = handler;
    },

    onFloor: (handler: (level: LevelDescriptor) => void): void => {
      onGround = handler;
    },

    onCharacter: (handler: (vitals: CharacterVitals) => void): void => {
      onSeat = handler;
      // The join snapshot is already in hand and is where a fresh seat's life,
      // mana and level arrive; without this the HUD would stay blank until the
      // first thing on the floor moved.
      const mine = room.state.players.get(room.sessionId);
      if (mine !== undefined) handler(readVitals(mine));
    },

    onProgression: (handler: (ledger: ProgressionLedger) => void): void => {
      room.onMessage(SERVER_MESSAGE.progression, (payload: ProgressionPayload): void => {
        // `ProgressionPayload` satisfies `ProgressionLedger` structurally and is
        // deliberately not parsed: every parser in `@d2/schema` exists because a
        // *client* said something, and this is the server reporting its own
        // ledger — `pong` is not parsed either.
        handler(payload);
      });
    },

    onRunToll: (handler: (toll: RunToll) => void): void => {
      room.onMessage(SERVER_MESSAGE.runToll, (payload: RunTollPayload): void => {
        // Converted at the edge rather than passed through, which is where this
        // differs from `onProgression` above: that one hands the payload on
        // because it satisfies `ProgressionLedger` structurally, and this one
        // goes through `./wire` because that is the module a unit test can reach
        // and this one is not (see the file header, and `vitest.config.ts`'s
        // exclusion list). The reader is where the next message follows.
        handler(readRunToll(payload));
      });
    },

    onInvite: (handler: (code: string) => void): void => {
      room.onMessage(SERVER_MESSAGE.invite, (payload: InvitePayload): void => {
        // Passed through, not parsed — the same call `onProgression` makes, and
        // `@d2/schema`'s own note on this message says so: *"the client does not
        // have to parse it — this is the server describing its own room"*.
        handler(payload.code);
      });
    },

    onClosed: (handler: (code: number) => void): void => {
      // `Room.onLeave` and not `onDrop`: the SDK routes 1001, 1005, 1006 and
      // 4010 to the drop channel and reconnects through them, and everything
      // else — 4200 among them — straight to here. Checked against
      // `@colyseus/sdk` 0.17's `Room.connect` rather than assumed, because the
      // whole of this feature rests on which of the two channels the room's
      // close arrives on.
      room.onLeave((code: number): void => {
        handler(code);
      });
    },

    send: (intent: PendingIntent): void => {
      const payload: MoveToPayload = { target: intent.target, sequence: intent.sequence };

      room.send(CLIENT_MESSAGE.moveTo, payload);
    },

    attack: (payload: AttackPayload): void => {
      room.send(CLIENT_MESSAGE.attack, payload);
    },

    pickUp: (payload: PickUpPayload): void => {
      room.send(CLIENT_MESSAGE.pickUp, payload);
    },

    useSkill: (payload: UseSkillPayload): void => {
      room.send(CLIENT_MESSAGE.useSkill, payload);
    },

    spendSkillPoint: (payload: SpendSkillPointPayload): void => {
      room.send(CLIENT_MESSAGE.spendSkillPoint, payload);
    },

    descend: (payload: DescendPayload): void => {
      room.send(CLIENT_MESSAGE.descend, payload);
    },

    leave: async (): Promise<void> => {
      // Consented, and awaited: the SDK settles this when the socket is closed,
      // and a caller waiting to reconnect the same character needs that moment
      // rather than the moment the message was sent. A socket that is already
      // gone is a leave that has happened, not a failure to report.
      await room.leave(true).catch(() => undefined);
    },
  };
};
